<template>
    <v-container fluid class="pa-4">
        <v-row class="mb-2">
            <v-col cols="12" class="d-flex align-center justify-space-between">
                <div>
                    <h1 class="text-h5 font-weight-bold">Driver Inspections (DVIR)</h1>
                    <p class="text-body-2 text-medium-emphasis mt-1">Driver DVIR submissions and the defect board</p>
                </div>
                <v-btn icon="mdi-refresh" variant="text" aria-label="Refresh" @click="refreshAll"/>
            </v-col>
        </v-row>

        <v-tabs :model-value="tab" color="primary" class="mb-3" @update:model-value="setTab">
            <v-tab value="inspections">Inspections</v-tab>
            <v-tab value="defects">
                Defects
                <v-badge v-if="criticalDefects" :content="`${criticalDefects} critical`" color="error" inline class="ml-1"/>
                <v-badge v-else-if="openDefects" :content="openDefects" color="error" inline class="ml-1"/>
            </v-tab>
        </v-tabs>

        <!-- Both stay mounted, so each keeps its filters and its part of the URL. -->
        <v-window :model-value="tab">
            <v-window-item value="inspections" eager>
                <InspectionList ref="inspectionList" @open="openInspection" @totals="onTotals"
                                @show-defects="setTab('defects')"/>
            </v-window-item>
            <v-window-item value="defects" eager>
                <DefectList ref="defectList" @photo="viewPhoto" @changed="refreshAll" @open-inspection="openInspection"/>
            </v-window-item>
        </v-window>

        <!-- Inspection detail dialog -->
        <v-dialog :model-value="detailOpen" max-width="720" scrollable content-class="inspection-print"
                  @update:model-value="(v) => { if (!v) closeInspection() }">
            <v-card v-if="!detail" class="pa-4">
                <v-skeleton-loader type="heading, list-item-two-line, list-item-two-line, list-item-two-line"/>
            </v-card>
            <v-card v-else>
                <v-card-title class="d-flex align-center ga-2 pt-4">
                    <span class="text-subtitle-1 font-weight-bold text-wrap">
                        {{ detail.asset?.label || 'Vehicle' }} - {{ typeLabel(detail.type) }} inspection
                    </span>
                    <v-spacer/>
                    <v-btn class="no-print" icon="mdi-printer" variant="text" size="small" aria-label="Print"
                           @click="printInspection"/>
                    <v-btn class="no-print" icon="mdi-close" variant="text" size="small" aria-label="Close"
                           @click="closeInspection"/>
                </v-card-title>
                <v-card-text>
                    <div class="d-flex flex-wrap align-center ga-2 text-body-2 text-medium-emphasis mb-3">
                        <span>{{ driverName(detail.driver) }}</span>
                        <span>&middot;</span>
                        <span>{{ fmtDateTime(detail.submitted_at, zone) }}</span>
                        <v-chip size="x-small" variant="flat" :color="statusColor(detail.status)">{{ statusLabel(detail.status) }}</v-chip>
                        <v-chip v-if="detail.gps_verified" size="x-small" variant="tonal" color="success"
                                prepend-icon="mdi-map-marker-check">GPS verified</v-chip>
                        <v-chip v-else size="x-small" variant="tonal" prepend-icon="mdi-map-marker-off">Not GPS verified</v-chip>
                    </div>

                    <!-- Key facts -->
                    <v-row dense class="mb-2">
                        <v-col cols="12" sm="4">
                            <div class="text-caption text-medium-emphasis">Odometer</div>
                            <div class="text-body-1">{{ odometerText }}</div>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <div class="text-caption text-medium-emphasis">Driver certified safe to operate</div>
                            <div class="text-body-1 d-flex align-center ga-1">
                                <template v-if="detail.safe_to_operate === true">
                                    <v-icon color="success" size="18">mdi-check-circle</v-icon> Yes
                                </template>
                                <template v-else-if="detail.safe_to_operate === false">
                                    <v-icon color="error" size="18">mdi-close-circle</v-icon> No
                                </template>
                                <span v-else class="text-medium-emphasis">Not answered</span>
                            </div>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <div class="text-caption text-medium-emphasis">GPS point</div>
                            <a v-if="mapsUrl" :href="mapsUrl" target="_blank" rel="noopener noreferrer"
                               class="text-body-1 text-primary d-inline-flex align-center ga-1">
                                <v-icon size="18">mdi-map-marker</v-icon>{{ coordsText }}
                            </a>
                            <div v-else class="text-body-1 text-medium-emphasis">Not recorded</div>
                        </v-col>
                    </v-row>

                    <div v-if="detail.notes" class="mb-3">
                        <div class="text-caption text-medium-emphasis">Driver notes</div>
                        <div class="text-body-2" style="white-space: pre-line">{{ detail.notes }}</div>
                    </div>

                    <!-- Defects found, with what to do about each -->
                    <template v-if="detail.defects?.length">
                        <h4 class="text-subtitle-2 font-weight-bold mb-1">Defects</h4>
                        <v-list density="compact" class="py-0 mb-3">
                            <v-list-item v-for="d in detail.defects" :key="d.id" class="px-0">
                                <template #prepend>
                                    <v-chip size="x-small" variant="flat" :color="sevColor(d.severity)" class="mr-2">
                                        {{ sevLabel(d.severity) }}
                                    </v-chip>
                                </template>
                                <v-list-item-title class="text-body-2">{{ d.label }}</v-list-item-title>
                                <v-list-item-subtitle>
                                    {{ defectStatusLabel(d.status) }}
                                    <span v-if="d.note"> &middot; {{ d.note }}</span>
                                </v-list-item-subtitle>
                                <template #append>
                                    <div class="d-flex flex-wrap align-center justify-end ga-1">
                                        <v-chip v-if="d.service_task_id" size="x-small" variant="outlined"
                                                prepend-icon="mdi-wrench" :to="workOrderRoute(d)">
                                            {{ d.service_task ? `WO: ${d.service_task.label || d.label}${woStatus(d)}` : 'Work order' }}
                                        </v-chip>
                                        <v-btn v-else-if="d.status !== 'resolved'" class="no-print" size="x-small"
                                               variant="tonal" color="primary" :loading="detailBusy === d.id + ':wo'"
                                               :disabled="!!detailBusy" @click="detailWorkOrder(d)">Work order</v-btn>
                                        <v-btn v-if="d.status !== 'resolved'" class="no-print" size="x-small"
                                               variant="text" color="success" :disabled="!!detailBusy"
                                               @click="resolveFromDetail(d)">Resolve</v-btn>
                                    </div>
                                </template>
                            </v-list-item>
                        </v-list>
                    </template>

                    <h4 class="text-subtitle-2 font-weight-bold mb-1">Checklist</h4>
                    <v-list density="compact" class="py-0">
                        <v-list-item v-for="it in detail.items" :key="it.id" class="px-0">
                            <template #prepend>
                                <v-icon :color="it.result === 'defect' ? 'error' : 'success'" size="20"
                                        :aria-label="it.result === 'defect' ? 'Defect' : 'OK'">
                                    {{ it.result === 'defect' ? 'mdi-alert-circle' : 'mdi-check-circle' }}
                                </v-icon>
                            </template>
                            <v-list-item-title class="text-body-2">{{ it.label }}</v-list-item-title>
                            <v-list-item-subtitle v-if="it.note">{{ it.note }}</v-list-item-subtitle>
                            <template #append>
                                <InspectionPhoto v-if="it.photo_path" :path="it.photo_path" :size="48"
                                                 :label="`Photo: ${it.label}`" @open="viewPhoto"/>
                            </template>
                        </v-list-item>
                    </v-list>

                    <div v-if="detail.signature_path" class="mt-4">
                        <div class="text-caption text-medium-emphasis mb-1">Driver signature</div>
                        <InspectionPhoto :path="detail.signature_path" :width="240" :height="90" contain
                                         label="Driver signature" @open="viewPhoto"/>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>

        <ResolveDefectDialog v-model="resolveOpen" :defects="resolving" @resolved="onDetailChanged"/>

        <!-- Photo viewer -->
        <v-dialog v-model="photoDialog" max-width="560">
            <v-card class="pa-2">
                <v-img v-if="photoUrl" :src="photoUrl" max-height="70vh" contain/>
                <div v-else class="pa-6 text-center"><v-progress-circular indeterminate/></div>
            </v-card>
        </v-dialog>

        <v-snackbar v-model="actionError" color="error" timeout="3000">{{ actionErrorMsg }}</v-snackbar>
    </v-container>
</template>

<script setup>
import {computed, onBeforeUnmount, ref, watch} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {send} from '@/composables/apiClient'
import InspectionList from '@/components/inspections/InspectionList.vue'
import DefectList from '@/components/inspections/DefectList.vue'
import InspectionPhoto from '@/components/inspections/InspectionPhoto.vue'
import ResolveDefectDialog from '@/components/inspections/ResolveDefectDialog.vue'
import {createDefectWorkOrder, workOrderRoute, workOrderStatusLabel} from '@/components/inspections/defectActions'
import {
    defectStatusLabel, driverName, fmtDateTime, one, patchQuery, sevColor, sevLabel, statusColor, statusLabel, typeLabel,
} from '@/components/inspections/inspectionFormat'

const route = useRoute()
const router = useRouter()

const inspectionList = ref(null)
const defectList = ref(null)

// The tab and the open inspection live in the URL with the filters.
const tab = computed(() => (one(route.query.tab) === 'defects' ? 'defects' : 'inspections'))
const setTab = (t) => patchQuery(router, {tab: t === 'defects' ? 'defects' : ''})

const openDefects = ref(0)
const criticalDefects = ref(0)
const zone = ref(null)
const onTotals = ({totals, zone: z}) => {
    openDefects.value = totals?.company_open_defects ?? 0
    criticalDefects.value = totals?.company_critical_defects ?? 0
    zone.value = z
}

// After a work order or a resolve, both lists and the totals change.
const refreshAll = () => {
    inspectionList.value?.refresh()
    defectList.value?.refresh()
}

const detailId = computed(() => (/^\d{1,18}$/.test(one(route.query.inspection)) ? one(route.query.inspection) : null))
const detailOpen = computed(() => detailId.value !== null)
const detail = ref(null)

const photoDialog = ref(false)
const photoUrl = ref('')

const actionError = ref(false)
const actionErrorMsg = ref('')

const flashError = (msg) => {
    actionErrorMsg.value = msg
    actionError.value = true
}

const openInspection = (id) => patchQuery(router, {inspection: id})
const closeInspection = () => {
    endPrint()
    patchQuery(router, {inspection: ''})
}

let detailSeq = 0
const loadDetail = async (id, quiet = false) => {
    const seq = ++detailSeq
    if (!quiet) detail.value = null
    if (!id) return
    try {
        const res = await send('get', `fleet/inspections/${id}`)
        if (seq === detailSeq) detail.value = res?.data?.data
    } catch (e) {
        if (seq !== detailSeq) return
        closeInspection()
        flashError('Could not load the inspection.')
    }
}
watch(detailId, (id) => loadDetail(id), {immediate: true})

const odometerText = computed(() => {
    const n = Number(detail.value?.odometer)
    return detail.value?.odometer !== null && detail.value?.odometer !== undefined && Number.isFinite(n)
        ? n.toLocaleString(undefined, {maximumFractionDigits: 1})
        : 'Not recorded'
})
const coords = computed(() => {
    const lat = Number(detail.value?.gps_lat)
    const lng = Number(detail.value?.gps_lng)
    if (detail.value?.gps_lat === null || detail.value?.gps_lat === undefined || !Number.isFinite(lat) || !Number.isFinite(lng)) return null
    return {lat, lng}
})
const coordsText = computed(() => (coords.value ? `${coords.value.lat.toFixed(5)}, ${coords.value.lng.toFixed(5)}` : ''))
const mapsUrl = computed(() => (coords.value ? `https://www.google.com/maps?q=${coords.value.lat},${coords.value.lng}` : ''))

const woStatus = (d) => {
    const s = workOrderStatusLabel(d.service_task?.status)
    return s ? ` (${s})` : ''
}

// Defect actions inside the dialog reload it in place and refresh both lists.
const detailBusy = ref(null)
const onDetailChanged = () => {
    loadDetail(detailId.value, true)
    refreshAll()
}
const detailWorkOrder = async (d) => {
    detailBusy.value = d.id + ':wo'
    try {
        if (await createDefectWorkOrder(d)) onDetailChanged()
    } finally {
        detailBusy.value = null
    }
}
const resolveOpen = ref(false)
const resolving = ref([])
const resolveFromDetail = (d) => {
    resolving.value = [{...d, asset: detail.value?.asset}]
    resolveOpen.value = true
}

// Scopes the print CSS to this print only; removed again once printing ends.
const PRINT_CLASS = 'printing-inspection'
const endPrint = () => document.documentElement.classList.remove(PRINT_CLASS)
const printInspection = () => {
    document.documentElement.classList.add(PRINT_CLASS)
    window.addEventListener('afterprint', endPrint, {once: true})
    window.print()
}
onBeforeUnmount(() => {
    window.removeEventListener('afterprint', endPrint)
    endPrint()
})

const viewPhoto = async (path) => {
    if (photoUrl.value) {
        URL.revokeObjectURL(photoUrl.value)  // free the previous blob
    }
    photoUrl.value = ''
    photoDialog.value = true
    try {
        const res = await send('get', 'fleet/inspection-photo', {path}, {responseType: 'blob'})
        photoUrl.value = URL.createObjectURL(res.data)
    } catch (e) {
        photoDialog.value = false
        flashError('Could not load the photo.')
    }
}

// Free the blob URL when the viewer closes or the page unmounts (not just on
// the next open) so viewing many photos doesn't accumulate object URLs.
const freePhoto = () => {
    if (photoUrl.value) {
        URL.revokeObjectURL(photoUrl.value)
        photoUrl.value = ''
    }
}
watch(photoDialog, (open) => {
    if (!open) freePhoto()
})
onBeforeUnmount(freePhoto)
</script>

<style>
/* Printing the open inspection: only while printInspection() has marked <html>, so other pages print as usual. */
@media print {
    /* Undo the scroll lock Vuetify puts on html while a dialog is open, or only one page prints */
    html.printing-inspection {
        position: static !important;
        top: auto !important;
        height: auto !important;
        overflow: visible !important;
    }
    html.printing-inspection body * {
        visibility: hidden !important;
    }

    html.printing-inspection .inspection-print, html.printing-inspection .inspection-print * {
        visibility: visible !important;
    }

    /* Let the dialog flow in the page so a long inspection runs across pages. */
    html.printing-inspection .v-overlay-container, html.printing-inspection .v-overlay,
    html.printing-inspection .v-dialog, html.printing-inspection .inspection-print {
        position: static !important;
        inset: auto !important;
        transform: none !important;
        width: 100% !important;
        max-width: 100% !important;
        height: auto !important;
        max-height: none !important;
        margin: 0 !important;
        overflow: visible !important;
    }

    html.printing-inspection .inspection-print .v-card, html.printing-inspection .inspection-print .v-card-text {
        overflow: visible !important;
        max-height: none !important;
        box-shadow: none !important;
    }

    html.printing-inspection .inspection-print .no-print, html.printing-inspection .v-overlay__scrim {
        display: none !important;
    }

    /* The app and any other overlay would still take space before the dialog. */
    html.printing-inspection body > *:not(.v-overlay-container),
    html.printing-inspection .v-overlay-container > .v-overlay:not(:has(.inspection-print)) {
        display: none !important;
    }
}
</style>
